/**
 * src/components/support/TicketTimeline.js
 *
 * Renders the replies array from a support ticket in a threaded timeline.
 * Uses distinct styles for user vs admin messages.
 */

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { User, ShieldCheck } from "lucide-react";
import { errorDev } from '../../utils/logger.js';

function fmtTime(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function TicketTimeline({ replies = [] }) {
  const { t, ready: i18nReady } = useTranslation("support");
  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  if (!replies.length) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
        {safeT("timeline_empty", "No messages yet.")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {replies.map((r, idx) => {
        const isAdmin = !!r.isAdmin;
        return (
          <div
            key={idx}
            className={`flex gap-3 ${isAdmin ? "flex-row" : "flex-row-reverse"}`}
          >
            {/* Avatar */}
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                isAdmin
                  ? "bg-green-100 dark:bg-green-900/30"
                  : "bg-gray-100 dark:bg-neutral-700"
              }`}
            >
              {isAdmin ? (
                <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[80%] rounded-xl px-4 py-3 ${
                isAdmin
                  ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  : "bg-gray-50 dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600"
              }`}
            >
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-gray-900 dark:text-white">
                  {isAdmin
                    ? r.sentBy || safeT("timeline_support", "Support")
                    : r.sentBy || safeT("timeline_you", "You")}
                </span>
                {isAdmin && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
                    {safeT("timeline_staff", "Staff")}
                  </span>
                )}
              </div>

              {/* Body */}
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {r.message}
              </p>

              {/* Timestamp */}
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 text-right">
                {fmtTime(r.createdAt)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
